import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import {
  FileText,
  Package,
  Calendar,
  DollarSign,
  Building2,
  Phone,
  MapPin,
  CheckCircle2,
  AlertCircle,
  Truck,
} from "lucide-react";
import ReactDatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import Logo from "../assets/SiteMaster.svg";
import { BASE_URL } from "utils/constant";
const QuotationForm = () => {
  const { token } = useParams();
  const [quotationData, setQuotationData] = useState(null);
  const [vendorDetails, setVendorDetails] = useState({
    vendor_name: "",
    address: "",
    phone: "",
  });
  const [lineItems, setLineItems] = useState([]);
  const [deliveryDate, setDeliveryDate] = useState("");
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchQuotation = async () => {
      try {
        const response = await fetch(
          `${BASE_URL}/inventory/rfq-invitations/${token}/`,
          {
            headers: { "Content-Type": "application/json" },
          }
        );

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        if (data.error) {
          setError(data.error);
          setLoading(false);
          return;
        }

        if (!data.lines || !Array.isArray(data.lines)) {
          throw new Error("Invalid data format received");
        }

        setQuotationData(data);
        setVendorDetails({
          vendor_name: data.vendor_name || "",
          address: data.vendor_address || "",
          phone: data.vendor_phone || "",
        });
        setLineItems(
          data.lines.map((line) => ({
            rfq_lines: line.id,
            material: line.material,
            quantity: line.quantity,
            unit_price: 0,
            tax_percentage: 0,
            tax_amount: 0,
            total_price: 0,
          }))
        );
        setLoading(false);
      } catch (err) {
        console.error("Fetch error:", err);
        setError(err.message || "Failed to fetch quotation data");
        setLoading(false);
      }
    };

    if (token) {
      fetchQuotation();
    } else {
      setError("Invalid quotation link");
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (success) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [success]);

  const handleVendorChange = (e) => {
    const { name, value } = e.target;
    setVendorDetails((prev) => ({ ...prev, [name]: value }));
  };

  const handleLineItemChange = (index, field, value) => {
    const updatedLines = [...lineItems];
    updatedLines[index] = {
      ...updatedLines[index],
      [field]:
        field === "unit_price" || field === "tax_percentage"
          ? parseFloat(value) || 0
          : value,
    };

    const unitPrice = updatedLines[index].unit_price || 0;
    const quantity = updatedLines[index].quantity || 0;
    const taxPercentage = updatedLines[index].tax_percentage || 0;
    const taxAmount = (unitPrice * quantity * taxPercentage) / 100;
    updatedLines[index].tax_amount = taxAmount;
    updatedLines[index].total_price = unitPrice * quantity + taxAmount;

    setLineItems(updatedLines);
  };

  const totalPrice = lineItems.reduce(
    (sum, item) => sum + (item.total_price || 0),
    0
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    if (!deliveryDate) {
      setError("Please provide a delivery date.");
      return;
    }

    const payload = {
      ...vendorDetails,
      delivery_date: deliveryDate,
      total_price: totalPrice,
      lines: lineItems,
    };

    try {
      const response = await fetch(
        `${BASE_URL}/inventory/rfq-invitations/${token}/submit-quotation/`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        }
      );
      const data = await response.json();
      if (data.error) {
        setError(data.error);
        return;
      }
      setSuccess("Quotation submitted successfully!");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      setError("Failed to submit quotation");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-blue-500 border-t-transparent"></div>
          <p className="mt-4 text-slate-600 font-medium">
            Loading quotation...
          </p>
        </div>
      </div>
    );
  }

  if (error && !quotationData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-slate-800 mb-2">
            Error Loading Quotation
          </h2>
          <p className="text-slate-600">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      <div className="bg-white border-b border-slate-200 shadow-sm sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <div className="flex flex-col items-start space-y-1">
            <img src={Logo} alt="SiteMaster Logo" className="h-10" />
            <p className="text-xs text-slate-500 hidden sm:block">
              Procurement Management
            </p>
          </div>
          <div className="text-right">
            <p className="text-sm font-bold text-slate-700">
              {quotationData?.rfq_number}
            </p>
            <p className="text-base text-slate-500">
              {quotationData?.organization_name}
            </p>
          </div>
        </div>
      </div>

      {success && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-white">
          <div className="bg-green-50 border border-green-200 rounded-2xl p-8 shadow-2xl max-w-md w-[90%] text-center">
            <CheckCircle2 className="w-12 h-12 text-green-600 mx-auto mb-3" />
            <h3 className="text-2xl font-bold text-green-900">Success!</h3>
            <p className="text-green-800 mt-2">{success}</p>
          </div>
        </div>
      )}

      <div className="max-w-6xl mx-auto p-4 sm:p-6 lg:p-8 py-8">
        {error && quotationData && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-xl p-4 flex items-start space-x-3 animate-[slideDown_0.3s_ease-out]">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-semibold text-red-900">Error</h3>
              <p className="text-red-700 text-sm">{error}</p>
            </div>
          </div>
        )}

        {!success && (
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className={`bg-white rounded-2xl shadow-lg overflow-hidden border border-slate-200 transition-all hover:shadow-xl ${success ? 'hidden' : ''}`}>
              <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-4">
                <div className="flex items-center space-x-2 text-white">
                  <Building2 className="w-5 h-5" />
                  <h2 className="text-xl font-semibold">Vendor Information</h2>
                </div>
              </div>
              <div className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <label className="flex items-center space-x-2 text-sm font-medium text-slate-700">
                      <Building2 className="w-4 h-4" />
                      <span>Vendor Name</span>
                    </label>
                    <input
                      type="text"
                      name="vendor_name"
                      value={vendorDetails.vendor_name}
                      onChange={handleVendorChange}
                      placeholder="Enter vendor name"
                      className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="flex items-center space-x-2 text-sm font-medium text-slate-700">
                      <Phone className="w-4 h-4" />
                      <span>Phone Number</span>
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      disabled={true}
                      value={vendorDetails.phone}
                      onChange={handleVendorChange}
                      placeholder="Phone number"
                      className="w-full px-4 py-3 border border-slate-300 rounded-lg bg-slate-50 text-slate-600 cursor-not-allowed"
                      required
                    />
                  </div>
                  <div className="space-y-2 md:col-span-2">
                    <label className="flex items-center space-x-2 text-sm font-medium text-slate-700">
                      <MapPin className="w-4 h-4" />
                      <span>Address</span>
                    </label>
                    <input
                      type="text"
                      name="address"
                      value={vendorDetails.address}
                      onChange={handleVendorChange}
                      placeholder="Enter complete address"
                      className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                      required
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className={`bg-white rounded-2xl shadow-lg overflow-hidden border border-slate-200 transition-all hover:shadow-xl ${success ? 'hidden' : ''}`}>
              <div className="bg-gradient-to-r from-emerald-600 to-emerald-700 px-6 py-4">
                <div className="flex items-center space-x-2 text-white">
                  <Calendar className="w-5 h-5" />
                  <h2 className="text-xl font-semibold">Delivery Details</h2>
                </div>
              </div>
              <div className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <label className="flex items-center space-x-2 text-sm font-medium text-slate-700">
                      <Calendar className="w-4 h-4" />
                      <span>Expected Delivery Date</span>
                    </label>
                    <div className="relative">
                      <ReactDatePicker
                        selected={deliveryDate ? new Date(deliveryDate) : null}
                        onChange={(date) => {
                          if (date) {
                            const year = date.getFullYear();
                            const month = String(date.getMonth() + 1).padStart(2, '0');
                            const day = String(date.getDate()).padStart(2, '0');
                            setDeliveryDate(`${year}-${month}-${day}`);
                          } else {
                            setDeliveryDate("");
                          }
                        }}
                        dateFormat="yyyy-MM-dd"
                        placeholderText="Select delivery date"
                        className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all"
                        onKeyDown={(e) => e.preventDefault()}
                        required
                        portalId="root"
                        popperPlacement="bottom-start"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label className="flex items-center space-x-2 text-sm font-medium text-slate-700">
                      <Truck className="w-4 h-4" />
                      <span>Delivery Address</span>
                    </label>
                    <div className="p-4 bg-slate-50 border border-slate-200 rounded-lg">
                      {quotationData?.sites ? (
                        <div className="space-y-1">
                          <p className="text-sm font-semibold text-slate-900">
                            {quotationData.site_name}
                          </p>
                          <p className="text-sm text-slate-600">
                            {quotationData.site_address}
                          </p>
                          <p className="text-sm text-slate-600">
                            {quotationData.city} - {quotationData.pincode}
                          </p>
                        </div>
                      ) : (
                        <div className="space-y-1">
                          <p className="text-sm font-semibold text-slate-900">
                            Warehouse / Organization Address
                          </p>
                          <p className="text-sm text-slate-600">
                            {quotationData?.organization_address || ""}
                          </p>
                          <p className="text-sm text-slate-600">
                            {quotationData?.organization_state || ""} - {quotationData?.organization_pincode || ""}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className={`bg-white rounded-2xl shadow-lg overflow-hidden border border-slate-200 transition-all hover:shadow-xl ${success ? 'hidden' : ''}`}>
              <div className="bg-gradient-to-r from-purple-600 to-purple-700 px-6 py-4">
                <div className="flex items-center space-x-2 text-white">
                  <Package className="w-5 h-5" />
                  <h2 className="text-xl font-semibold">Quotation Lines</h2>
                </div>
              </div>
              <div className="p-6">
                <div className="hidden lg:block overflow-x-auto -mx-6 sm:mx-0">
                  <div className="inline-block min-w-full align-middle">
                    <table className="min-w-full divide-y divide-slate-200">
                      <thead className="bg-slate-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider">
                            Material
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider">
                            Specification
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider">
                            Brand
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider">
                            Qty
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider">
                            Unit Price
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider">
                            Tax %
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider">
                            Tax Amt
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider">
                            Total
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-slate-200">
                        {quotationData?.lines?.map((line, index) => (
                          <tr
                            key={line.id}
                            className="hover:bg-slate-50 transition-colors"
                          >
                            <td className="px-4 py-4 text-sm text-slate-900 font-medium">
                              {line.material_name || 'N/A'}
                            </td>
                            <td className="px-4 py-4 text-sm text-slate-600">
                              {line.specification || 'N/A'}
                            </td>
                            <td className="px-4 py-4 text-sm text-slate-600">
                              {line.brand_name || 'N/A'}
                            </td>
                            <td className="px-4 py-4 text-sm text-slate-900 whitespace-nowrap">
                              {line.quantity} {line.uom}
                            </td>
                            <td className="px-4 py-4">
                              <input
                                type="number"
                                value={lineItems[index]?.unit_price || ""}
                                onChange={(e) =>
                                  handleLineItemChange(
                                    index,
                                    "unit_price",
                                    e.target.value
                                  )
                                }
                                className="w-24 px-2 py-1.5 text-sm border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                                min="0"
                                step="0.01"
                                required
                              />
                            </td>
                            <td className="px-4 py-4">
                              <input
                                type="number"
                                value={lineItems[index]?.tax_percentage || ""}
                                onChange={(e) =>
                                  handleLineItemChange(
                                    index,
                                    "tax_percentage",
                                    e.target.value
                                  )
                                }
                                className="w-20 px-2 py-1.5 text-sm border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                              />
                            </td>
                            <td className="px-4 py-4 text-sm text-slate-900">
                              ₹{(lineItems[index]?.tax_amount || 0).toFixed(2)}
                            </td>
                            <td className="px-4 py-4 text-sm font-semibold text-slate-900">
                              ₹{(lineItems[index]?.total_price || 0).toFixed(2)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                <div className="lg:hidden space-y-4">
                  {quotationData?.lines?.map((line, index) => (
                    <div
                      key={line.id}
                      className="bg-white rounded-xl p-4 border border-slate-200 shadow-sm"
                    >
                      <div className="mb-2 pb-2 border-b border-slate-200 space-y-2.5">
                        <div className="flex items-baseline gap-2">
                          <span className="text-xs font-bold text-slate-500 uppercase tracking-wider flex-shrink-0">
                            Material:
                          </span>
                          <span className="text-sm font-semibold text-slate-900 flex-1">
                            {line.material_name || 'N/A'}
                          </span>
                        </div>
                        <div className="flex items-baseline gap-2">
                          <span className="text-xs font-bold text-slate-500 uppercase tracking-wider flex-shrink-0">
                            Specification:
                          </span>
                          <span className="text-sm text-slate-700 flex-1">
                            {line.specification || 'N/A'}
                          </span>
                        </div>
                        <div className="flex items-baseline gap-2">
                          <span className="text-xs font-bold text-slate-500 uppercase tracking-wider flex-shrink-0">
                            Brand:
                          </span>
                          <span className="text-sm text-slate-700 flex-1">
                            {line.brand_name || 'N/A'}
                          </span>
                        </div>
                        <div className="flex items-baseline gap-2">
                          <span className="text-xs font-bold text-slate-500 uppercase tracking-wider flex-shrink-0">
                            Qty:
                          </span>
                          <span className="text-sm font-semibold text-slate-900 flex-1">
                            {line.quantity} {line.uom}
                          </span>
                        </div>
                      </div>

                      <div className="space-y-3">
                        <div>
                          <label className="block text-xs font-medium text-slate-700 mb-1">
                            Unit Price
                          </label>
                          <input
                            type="number"
                            value={lineItems[index]?.unit_price || ""}
                            onChange={(e) =>
                              handleLineItemChange(
                                index,
                                "unit_price",
                                e.target.value
                              )
                            }
                            className="w-full px-3 py-2 text-sm border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                            min="0"
                            step="0.01"
                            placeholder="Enter unit price"
                            required
                          />
                        </div>

                        <div>
                          <label className="block text-xs font-medium text-slate-700 mb-1">
                            Tax Percentage
                          </label>
                          <input
                            type="number"
                            value={lineItems[index]?.tax_percentage || ""}
                            onChange={(e) =>
                              handleLineItemChange(
                                index,
                                "tax_percentage",
                                e.target.value
                              )
                            }
                            className="w-full px-3 py-2 text-sm border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                            placeholder="Enter tax %"
                          />
                        </div>

                        <div className="pt-2 border-t border-slate-300">
                          <div className="flex justify-between items-center mb-1">
                            <span className="text-sm text-slate-600">Tax Amount:</span>
                            <span className="text-sm font-medium text-slate-900">
                              ₹{(lineItems[index]?.tax_amount || 0).toFixed(2)}
                            </span>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="text-sm font-semibold text-slate-700">Total:</span>
                            <span className="text-lg font-bold text-purple-700">
                              ₹{(lineItems[index]?.total_price || 0).toFixed(2)}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="mt-3 pt-3 border-t border-slate-200">
                  <div className="flex justify-end items-center space-x-4">
                    <div className="text-right">
                      <p className="text-sm text-slate-600 mb-1">
                        Total Quotation Amount
                      </p>
                      <div className="flex items-center space-x-2">
                        <p className="text-3xl font-bold text-slate-900">
                          ₹{totalPrice.toFixed(2)}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className={`flex justify-end ${success ? 'hidden' : ''}`}>
              <button
                type="submit"
                className="group relative px-8 py-4 bg-gradient-to-r from-blue-600 to-blue-700 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl hover:from-blue-700 hover:to-blue-800 focus:outline-none focus:ring-4 focus:ring-blue-300 transition-all transform hover:scale-105 active:scale-95"
              >
                <span className="flex items-center space-x-2">
                  <FileText className="w-5 h-5" />
                  <span>Submit Quotation</span>
                </span>
              </button>
            </div>
          </form>
        )}

        <div className="mt-12 text-center text-sm text-slate-500">
          <p>
            Powered by{" "}
            <span className="font-semibold text-slate-700">SiteMaster</span>
          </p>
        </div>
      </div>
    </div>
  );
};

export default QuotationForm;